/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { ActionType } from 'cio/models/action';
import { MEASURES, Measure, ALL_CHANNELS, Channel } from 'cio/utils/metrics/common';
import { findDenominator, MeasureType } from 'cio/utils/metrics/measure';
import { CUSTOM_CONVERTED_DENOMINATOR, ProcessedMetricItem } from 'cio/utils/metrics/analysis';
import actionInfo, { deliveryStatesAndActionsPerType, MESSAGING_TYPES } from 'cio/utils/action-info';
import { Metric } from 'cio/utils/metrics/models/metric';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { SafeString } from 'handlebars';
import { measureLabel } from 'cio/helpers/measure-label';

interface MultiMetricTooltipArgs {
  measure: Measure;
  measureType?: MeasureType;
  data: ProcessedMetricItem['data'];
  metricData?: Partial<Record<Channel, Metric>>;
  condition?: boolean;
  triggerClassNames?: string;
  contentClassNames?: string;
  actionType?: ActionType;
  direction?: string;
  channels?: string[];
  showRate?: boolean;
  optionalConversions?: Record<ActionType, boolean>;
  extraContent?: SafeString;
}

let ALL_MEASURES = [...MEASURES, 'clicked_to_opened'];

export default class MultiMetricTooltip extends Component<MultiMetricTooltipArgs> {
  @tracked data:
    | ProcessedMetricItem['data']
    | { per_channel_metrics: Partial<Record<Channel, Record<string, number>>> } = { per_channel_metrics: {} };

  constructor(owner: unknown, args: MultiMetricTooltipArgs) {
    super(owner, args);

    this.mapMetricData();
  }

  @action
  mapMetricData() {
    if (this.args.metricData == null) {
      this.data = this.args.data;
    } else {
      this.data = {
        per_channel_metrics: Object.keys(this.args.metricData).reduce<Partial<Record<Channel, Record<string, number>>>>(
          (acc, channel) => {
            let values: Record<string, number> = {};

            ALL_MEASURES.forEach((measure) => {
              values[`total_${measure}`] = this.args.metricData?.[channel as Channel]?.[measure as Measure]?.total ?? 0;
            });

            acc[channel as Channel] = values;

            return acc;
          },
          {},
        ),
      };
    }
  }

  get showRate() {
    return this.args.showRate ?? true;
  }

  get measureType() {
    return this.args.measureType ?? MeasureType.DeliveryMetrics;
  }

  get direction() {
    return this.args.direction ?? 'top';
  }

  get condition() {
    return this.args.condition ?? true;
  }

  get totalAccessor() {
    return `${this.measureType}.total_${this.args.measure}`;
  }

  get rateAccessor() {
    return `${this.measureType}.${this.args.measure}_rate`;
  }

  @cached
  get relevantChannels() {
    return MESSAGING_TYPES.reduce<string[]>((acc, type) => {
      let channel = type.replace('_action', '');
      let states = deliveryStatesAndActionsPerType(this.args.optionalConversions?.[type] ?? false)[type] ?? [];
      if (states.includes(this.args.measure) && (this.args.channels == null || this.args.channels.includes(channel))) {
        acc = [...new Set([...acc, channel])];
      }

      return acc;
    }, []).sort((a, b) => ALL_CHANNELS.indexOf(a as any) - ALL_CHANNELS.indexOf(b as any));
  }

  get subscriptionTopicWithData() {
    let topicMetrics = this.args.data?.per_subscription_topic_metrics ?? {};
    if (Object.keys(topicMetrics).length === 0) {
      return [];
    }
    return Object.entries(topicMetrics)
      .map(([k, v]) => ({
        name: k,
        value: v,
      }))
      .filter((el) => el.value !== 0);
  }

  @cached
  get channelsWithData() {
    if (this.args.measureType === MeasureType.JourneyMetrics) {
      return [];
    }

    return this.relevantChannels.map((channel: Channel) => {
      let channelData = this.data.per_channel_metrics[channel];

      let actionType = `${channel}_action` as ActionType;
      let info = actionInfo(actionType);

      let numeratorValue = (channelData?.[`total_${this.args.measure}`] ?? 0) as number;

      let denominator = findDenominator(this.args.measure as Measure, MeasureType.DeliveryMetrics, actionType);
      let denominatorLabel = measureLabel([denominator, MeasureType.DeliveryMetrics]);

      // For "converted" metrics, use "converted_denominator" instead of the
      // usual denominator, if present, to only account for
      // campaigns/newsletters/messages that actually can convert.
      if (this.args.measure === 'converted' && channelData?.[CUSTOM_CONVERTED_DENOMINATOR] != null) {
        denominator = CUSTOM_CONVERTED_DENOMINATOR as any;
      }

      let denominatorValue = (channelData?.[`total_${denominator}`] ?? 0) as number;

      return {
        actionTypeShortName: info.shortName,
        numeratorValue,
        denominatorValue,
        denominatorLabel,
      };
    });
  }

  @cached
  get shouldShowDenominatorNotice() {
    if (this.args.measureType === MeasureType.JourneyMetrics) {
      return false;
    }

    return this.relevantChannels.some((channel: Channel) => {
      let denominator = findDenominator(
        this.args.measure,
        MeasureType.DeliveryMetrics,
        `${channel}_action` as ActionType,
      );

      if (denominator !== 'delivered') {
        return false;
      }

      let numeratorTotal = (this.data?.per_channel_metrics[channel]?.[`total_${this.args.measure}`] as number) ?? 0;

      if (numeratorTotal === 0) {
        return false;
      }

      let denominatorTotal = (this.data?.per_channel_metrics[channel]?.[`total_${denominator}`] ?? null) as
        | number
        | null;

      return denominatorTotal === 0 || denominatorTotal == null;
    });
  }

  get contentClassNames() {
    let classes: string[] = [];

    if (this.args.contentClassNames != null) {
      classes = [this.args.contentClassNames];
    }

    if (this.shouldShowDenominatorNotice) {
      classes = [...classes, 'max-w-44'];
    }

    return classes.join(' ');
  }
}
